import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getSimpleAndAPIScoreMap } from '../../ReviewManage/reviewManageSidepanel/dataHooks';
import { getTalentPlanningSingleRowTabularDataApi } from '../dataHooks';
import { useCalibrationViewPanelQueryParams } from './sidepanelStateHooks';

const getCalibrationViewSidepanelInfoApi = ({ calibrationViewId, empId }) => {
  const url = endpoints.performanceReview.talentPlanning.panel;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: { empId, calibrationViewId },
  });
};

export const useGetCalibrationViewSidepanelInfo = () => {
  const { cVPanelCalibrationViewId: calibrationViewId, cVPanelEmpId: empId } = useCalibrationViewPanelQueryParams();

  return useQuery(
    [REACT_QUERY_KEYS.talentPlanning.sidepanel, calibrationViewId, empId],
    () =>
      getCalibrationViewSidepanelInfoApi({ calibrationViewId, empId }).then((res) => {
        const [calibrationScoreMap, calibrationApiScoreMap] = res?.entity?.metadata?.isCalibrationByLabel
          ? getSimpleAndAPIScoreMap({
              scoreMap: res.entity.scoreMap,
              answerType: 'DROP_DOWN',
            })
          : getSimpleAndAPIScoreMap({
              scoreMap: res.entity.scoreMapNumeric,
              answerType: 'NUMBER_RATING',
            });

        const [finalScoreMap] = getSimpleAndAPIScoreMap({
          scoreMap: res.entity.scoreMap,
          answerType: res.entity.metadata?.calibrationScoreSettings?.answerType,
        });

        const [finalScoreMapNumeric] = getSimpleAndAPIScoreMap({
          scoreMap: res.entity.scoreMapNumeric,
          answerType: 'NUMBER_RATING',
        });

        return {
          ...res.entity,
          finalScoreMap,
          finalScoreMapNumeric,
          calibrationScoreMap,
          calibrationApiScoreMap,
          completeScoreMap: res.entity.scoreMap,
        };
      }),
    {
      enabled: !!calibrationViewId && !!empId,
    }
  );
};

const getCalibrationHistory = ({ empId, reviewCycleId, feedbackFormId }) => {
  const url = endpoints.performanceReview.manage.calibrationHistory(empId);
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      feedbackFormId,
      reviewCycleId,
      isCalibrationView: true,
    },
  });
};

export const useGetCalibrateHistory = ({ reviewCycleId, empId, feedbackFormId }) =>
  useQuery(
    [REACT_QUERY_KEYS.talentPlanning.sidepanelCalibrationHistory, empId, reviewCycleId, feedbackFormId],
    () =>
      getCalibrationHistory({ empId, reviewCycleId, feedbackFormId }).then((res) => {
        const mergedComments = {};
        // following code is to sort the comments based on reCalibrationTime in descending order
        const commentsLatestFirst = res.entity.data
          .map((i) => i)
          .sort((a, b) => b.reCalibrationTime - a.reCalibrationTime);

        commentsLatestFirst.forEach((comm) => {
          if (!(`${comm.reCalibrationTime}-${comm.isCalibrated}` in mergedComments)) {
            mergedComments[`${comm.reCalibrationTime}-${comm.isCalibrated}`] = [];
          }
          mergedComments[`${comm.reCalibrationTime}-${comm.isCalibrated}`].push(comm);
        });

        return {
          comments: mergedComments,
        };
      }),
    {
      enabled: !!reviewCycleId && !!empId && !!feedbackFormId,
      staleTime: 60000,
    }
  );

const getReviewHistory = ({ empId, calibrationViewId }) => {
  const url = endpoints.performanceReview.talentPlanning.reviewHistory;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { empId, calibrationViewId },
  });
};

export const useGetCalibrationViewReviewHistory = () => {
  const { cVPanelCalibrationViewId: calibrationViewId, cVPanelEmpId: empId } = useCalibrationViewPanelQueryParams();

  return useQuery(
    [REACT_QUERY_KEYS.talentPlanning.sidepanelReviewHistory, empId, calibrationViewId],
    () => getReviewHistory({ empId, calibrationViewId }).then((res) => res.entity),
    {
      enabled: !!empId && !!calibrationViewId,
    }
  );
};

export const useInvalidateCalibrationSidepanelData = () => {
  const queryClient = useQueryClient();

  const { cVPanelCalibrationViewId: calibrationViewId, cVPanelEmpId: empId } = useCalibrationViewPanelQueryParams();

  return useCallback(() => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.sidepanelReviewHistory, empId, calibrationViewId]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.sidepanelCalibrationHistory]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.sidepanel, calibrationViewId, empId]);
  }, [calibrationViewId, empId, queryClient]);
};

export const useUpdateCalibrationViewSingleRowData = () => {
  const queryClient = useQueryClient();

  return useCallback(
    ({ calibrationViewId, empId, cohortDetails }) => {
      getTalentPlanningSingleRowTabularDataApi({ calibrationViewId, empId }).then((res) => {
        queryClient.setQueryData(
          [REACT_QUERY_KEYS.talentPlanning.tabularData, cohortDetails?.id, calibrationViewId],
          ({ columns, values }) => {
            const { employeeId, row: newRowData } = res.entity ?? {};
            const newValues = values.map((row) => {
              if (row.EMPLOYEE.id === employeeId) {
                return newRowData;
              }
              return row;
            });
            return { columns, values: newValues };
          }
        );
      });
    },
    [queryClient]
  );
};
